import { useCallback } from 'react';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import type { PlatformsType } from '@wix/cli-app-installer';
import { useProjectModel } from '../model.js';
import { createRedirectLink } from '../components/PlatformLink/RedirectLink.js';
import { writeDeploymentTopologyEnvironment } from '../files/topology.js';

interface UpdateDeploymentTopologyForSiteProps {
  siteUrl: string;
  versionOverrideId: string;
  environment: DeploymentTopologyEnvironmentType;
}

export function useUpdateDeploymentTopologyForSite(siteId: string) {
  const {
    model: { projectFolder },
  } = useProjectModel();

  return useCallback(
    async ({
      environment,
      siteUrl,
      versionOverrideId,
    }: UpdateDeploymentTopologyForSiteProps) => {
      await writeDeploymentTopologyEnvironment(projectFolder, environment, {
        id: versionOverrideId,
        site: { main: siteUrl },
        dashboard: {
          main: `https://manage.wix.com/dashboard/${siteId}?apps-override=${versionOverrideId}`,
        },
      });
    },
    [projectFolder, siteId]
  );
}

interface UpdateDeploymentTopologyForAppProps {
  versionOverrideId: string;
  environment: DeploymentTopologyEnvironmentType;
}

export function useUpdateDeploymentTopologyForApp(siteId?: string) {
  const {
    model: { projectFolder, config },
  } = useProjectModel();

  return useCallback(
    async ({
      environment,
      versionOverrideId,
    }: UpdateDeploymentTopologyForAppProps) => {
      const makeUrl = (platform: PlatformsType) =>
        createRedirectLink({
          platform,
          appId: config.appId,
          versionOverrideId,
          siteId,
        });

      await writeDeploymentTopologyEnvironment(projectFolder, environment, {
        id: versionOverrideId,
        site: { main: makeUrl('site') },
        editor: { main: makeUrl('editor') },
        dashboard: { main: makeUrl('dashboard') },
      });
    },
    [projectFolder, config.appId, siteId]
  );
}
